import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Block, isSection, isPrestation } from '../types/Block';
import { BlockCalculationService } from '../services';

export interface ComparisonSlot {
  phaseId: string;
  phaseName: string;
  versionId: string;
  versionName: string;
  blocks: Block[];
}

interface VersionComparisonPanelProps {
  slots: ComparisonSlot[]; // first = base de référence
  onClose: () => void;
}

interface ComparisonRow {
  type: 'lot' | 'prestation';
  label: string;
  prices: Record<string, number>; // `${phaseId}/${versionId}` → total HT
}

function slotKey(slot: ComparisonSlot) {
  return `${slot.phaseId}/${slot.versionId}`;
}

function buildRows(slots: ComparisonSlot[]): ComparisonRow[] {
  if (slots.length === 0) return [];
  const base = slots[0];
  const rows: ComparisonRow[] = [];

  const lots = base.blocks
    .filter(b => isSection(b) && b.level === 0)
    .sort((a, b) => a.order - b.order);

  lots.forEach(lot => {
    const lotPrices: Record<string, number> = {};
    slots.forEach(slot => {
      const match = slot.blocks.find(b => isSection(b) && b.level === 0 && b.content.titre === lot.content.titre);
      lotPrices[slotKey(slot)] = match
        ? BlockCalculationService.calculatePrestationTotal(match, slot.blocks)
        : 0;
    });
    rows.push({ type: 'lot', label: lot.content.titre, prices: lotPrices });

    const prestations = base.blocks
      .filter(b => isPrestation(b) && b.parentId === lot.id)
      .sort((a, b) => a.order - b.order);

    prestations.forEach(p => {
      const prestPrices: Record<string, number> = {};
      slots.forEach(slot => {
        const matchLot = slot.blocks.find(b => isSection(b) && b.level === 0 && b.content.titre === lot.content.titre);
        if (matchLot) {
          const matchP = slot.blocks.find(b =>
            isPrestation(b) && b.parentId === matchLot.id && b.content.intitule === p.content.intitule
          );
          prestPrices[slotKey(slot)] = matchP
            ? BlockCalculationService.parsePrice(matchP.content.prixTotal ?? '0')
            : 0;
        } else {
          prestPrices[slotKey(slot)] = 0;
        }
      });
      rows.push({ type: 'prestation', label: p.content.intitule || '—', prices: prestPrices });
    });
  });

  return rows;
}

function grandTotals(slots: ComparisonSlot[]): Record<string, number> {
  const result: Record<string, number> = {};
  slots.forEach(slot => {
    result[slotKey(slot)] = slot.blocks
      .filter(b => isSection(b) && b.level === 0)
      .reduce((sum, lot) => sum + BlockCalculationService.calculatePrestationTotal(lot, slot.blocks), 0);
  });
  return result;
}

const Delta: React.FC<{ base: number; value: number }> = ({ base, value }) => {
  if (base === 0 && value === 0) return <span className="text-gray-300 tabular-nums">—</span>;
  const delta = value - base;
  const pct = base !== 0 ? Math.round((delta / base) * 100) : null;
  if (delta === 0) return <span className="text-gray-400 text-xs tabular-nums">=</span>;
  const isPos = delta > 0;
  return (
    <span className={`text-xs font-medium tabular-nums ${isPos ? 'text-red-500' : 'text-emerald-600'}`}>
      {isPos ? '+' : ''}{pct !== null ? `${pct}%` : `${BlockCalculationService.formatPrice(delta)} €`}
    </span>
  );
};

const VersionComparisonPanel: React.FC<VersionComparisonPanelProps> = ({ slots, onClose }) => {
  const rows = buildRows(slots);
  const totals = grandTotals(slots);
  const base = slots[0];
  const others = slots.slice(1);

  if (slots.length < 2) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
      {/* Header */}
      <div className="flex items-center border-b-2 border-gray-200 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wider">
        <div className="flex-1 min-w-0 px-4 py-2.5 pl-10 flex items-center gap-2">
          <span>Désignation</span>
        </div>
        {/* Colonne base */}
        <div className="w-40 flex-shrink-0 px-3 py-2.5 border-l border-gray-200">
          <div className="text-right text-violet-500">{base.versionName}</div>
          <div className="text-right text-[10px] text-gray-400 font-normal normal-case">{base.phaseName}</div>
        </div>
        {/* Colonnes autres */}
        {others.map(slot => (
          <React.Fragment key={slotKey(slot)}>
            <div className="w-36 flex-shrink-0 px-3 py-2.5 border-l border-gray-200">
              <div className="text-right">{slot.versionName}</div>
              <div className="text-right text-[10px] text-gray-400 font-normal normal-case">{slot.phaseName}</div>
            </div>
            <div className="w-20 flex-shrink-0 px-3 py-2.5 border-l border-dashed border-gray-200 text-right text-gray-300 normal-case font-normal">
              écart
            </div>
          </React.Fragment>
        ))}
        <button
          onClick={onClose}
          className="w-10 flex-shrink-0 flex items-center justify-center py-2.5 border-l border-gray-200 text-gray-300 hover:text-gray-500 transition-colors"
          title="Fermer la comparaison"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Corps */}
      <div className="divide-y divide-gray-100">
        {rows.map((row, i) => {
          const isLot = row.type === 'lot';
          const basePrice = row.prices[slotKey(base)] ?? 0;
          return (
            <div
              key={i}
              className={`flex items-center ${
                isLot ? 'bg-gray-50 border-l-[3px] border-l-violet-300' : 'hover:bg-gray-50/80'
              } transition-colors`}
            >
              <div className={`flex-1 min-w-0 px-4 py-2 truncate ${isLot ? 'pl-4 font-semibold text-sm text-gray-700' : 'pl-10 text-sm text-gray-600'}`}>
                {row.label || '—'}
              </div>
              <div className="w-40 flex-shrink-0 px-3 py-2 text-right border-l border-gray-100 tabular-nums">
                {basePrice > 0
                  ? <span className={`text-sm ${isLot ? 'font-semibold text-violet-600' : 'text-gray-700'}`}>
                      {BlockCalculationService.formatPrice(basePrice)} €
                    </span>
                  : <span className="text-gray-300 text-sm">—</span>
                }
              </div>
              {others.map(slot => {
                const vPrice = row.prices[slotKey(slot)] ?? 0;
                return (
                  <React.Fragment key={slotKey(slot)}>
                    <div className="w-36 flex-shrink-0 px-3 py-2 text-right border-l border-gray-100 tabular-nums">
                      {vPrice > 0
                        ? <span className={`text-sm ${isLot ? 'font-semibold text-gray-600' : 'text-gray-600'}`}>
                            {BlockCalculationService.formatPrice(vPrice)} €
                          </span>
                        : <span className="text-gray-300 text-sm">—</span>
                      }
                    </div>
                    <div className="w-20 flex-shrink-0 px-3 py-2 text-right border-l border-dashed border-gray-100 flex items-center justify-end">
                      <Delta base={basePrice} value={vPrice} />
                    </div>
                  </React.Fragment>
                );
              })}
              <div className="w-10 flex-shrink-0 border-l border-gray-100" />
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="flex items-center border-t-2 border-gray-200 bg-gray-50 font-semibold">
        <div className="flex-1 min-w-0 px-4 py-3 text-sm text-gray-700 pl-10 uppercase tracking-wide">Total HT</div>
        <div className="w-40 flex-shrink-0 px-3 py-3 text-right border-l border-gray-200 tabular-nums text-sm text-violet-700">
          {BlockCalculationService.formatPrice(totals[slotKey(base)] ?? 0)} €
        </div>
        {others.map(slot => {
          const vTotal = totals[slotKey(slot)] ?? 0;
          const baseTotal = totals[slotKey(base)] ?? 0;
          return (
            <React.Fragment key={slotKey(slot)}>
              <div className="w-36 flex-shrink-0 px-3 py-3 text-right border-l border-gray-200 tabular-nums text-sm text-gray-600">
                {BlockCalculationService.formatPrice(vTotal)} €
              </div>
              <div className="w-20 flex-shrink-0 px-3 py-3 text-right border-l border-dashed border-gray-200 flex items-center justify-end">
                <Delta base={baseTotal} value={vTotal} />
              </div>
            </React.Fragment>
          );
        })}
        <div className="w-10 flex-shrink-0 border-l border-gray-200" />
      </div>
    </div>
  );
};

export default VersionComparisonPanel;
